import { Controller } from "@hotwired/stimulus"

// Adds .is-visible to any [data-reveal] descendant when it scrolls into view.
// Also rotates the headline words ([data-reveal-target="rotatorWord"]) on a timer.
// Honours `prefers-reduced-motion`: shows everything immediately, no rotation.
export default class extends Controller {
  static targets = ["rotatorWord"]

  connect() {
    this.reduced = window.matchMedia("(prefers-reduced-motion: reduce)").matches
    this.targets_ = Array.from(this.element.querySelectorAll("[data-reveal]"))

    if (this.reduced) {
      this.targets_.forEach((el) => { el.classList.add("is-visible") })
      return
    }

    this.observer = new IntersectionObserver(this.#handleEntries.bind(this), {
      root: null,
      rootMargin: "0px 0px -8% 0px",
      threshold: 0.12,
    })

    this.targets_.forEach((el) => { this.observer.observe(el) })

    // After Turbo Drive visits (e.g. locale switch → redirect back), layout can settle
    // after `observe()`; some browsers skip the initial IntersectionObserver callback for
    // nodes already on-screen, so `[data-reveal]` blocks stay at opacity: 0 until scroll.
    this.#scheduleInitialRevealFlush()

    // Headline word rotator: cycle the .is-active class every ~2.4s
    if (this.hasRotatorWordTarget && this.rotatorWordTargets.length > 1) {
      this.rotatorIndex = 0
      this.rotatorTimer = window.setInterval(() => {
        this.rotatorIndex = (this.rotatorIndex + 1) % this.rotatorWordTargets.length
        this.rotatorWordTargets.forEach((el, i) => {
          el.classList.toggle("is-active", i === this.rotatorIndex)
        })
      }, 2400)
    }
  }

  disconnect() {
    if (this.observer) this.observer.disconnect()
    if (this.rotatorTimer) window.clearInterval(this.rotatorTimer)
  }

  #handleEntries(entries) {
    for (const entry of entries) {
      if (entry.isIntersecting) {
        entry.target.classList.add("is-visible")
        this.observer.unobserve(entry.target)
      }
    }
  }

  /** Mirrors IntersectionObserver options below; runs after paint so rects are non-zero. */
  #scheduleInitialRevealFlush() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => this.#flushVisibleTargets())
    })
  }

  #flushVisibleTargets() {
    if (!this.observer) return

    const rootBottom = window.innerHeight * 0.92 // rootMargin: 0px 0px -8% 0px
    const rootRight = window.innerWidth

    for (const el of this.targets_) {
      if (el.classList.contains("is-visible")) continue

      const r = el.getBoundingClientRect()
      if (r.width <= 0 || r.height <= 0) continue

      const iw = Math.min(r.right, rootRight) - Math.max(r.left, 0)
      const ih = Math.min(r.bottom, rootBottom) - Math.max(r.top, 0)
      if (iw <= 0 || ih <= 0) continue

      const intersectionArea = iw * ih
      const elementArea = r.width * r.height
      const ratio = elementArea > 0 ? intersectionArea / elementArea : 0
      if (ratio >= 0.12) {
        el.classList.add("is-visible")
        this.observer.unobserve(el)
      }
    }
  }
}
